/**
 * <tuft-lazy-video>
 *
 * Web Component que envuelve un <video> de Shopify (output de `video_tag`)
 * dentro de un <template> y lo hidrata cuando entra al viewport.
 *
 * Uso (Liquid):
 *   <tuft-lazy-video>
 *     <img class="tuft-lazy-video__poster" src="..." />
 *     <template>
 *       {{ video | video_tag: ... }}
 *     </template>
 *   </tuft-lazy-video>
 *
 * Atributos opcionales:
 *   - root-margin="200px 0px"   margen del IntersectionObserver
 *   - threshold="0.01"          threshold del IntersectionObserver
 *
 * API pública:
 *   element.hydrate()           fuerza la carga inmediata (precarga manual)
 *
 * Eventos:
 *   tuft-lazy-video:ready       se emite (bubbles) cuando el video se hidrató
 */

(function () {
  class TuftLazyVideo extends HTMLElement {
    constructor() {
      super();
      this._hydrated = false;
    }

    connectedCallback() {
      if (this.dataset.upgraded) return;
      this.dataset.upgraded = '1';

      this._template = this.querySelector(':scope > template');
      if (!this._template) return;

      var rootMargin = this.getAttribute('root-margin') || '200px 0px';
      var threshold = parseFloat(this.getAttribute('threshold') || '0.01');

      this._io = new IntersectionObserver(this._onIntersect.bind(this), {
        rootMargin: rootMargin,
        threshold: threshold,
      });
      this._io.observe(this);
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
    }

    _onIntersect(entries) {
      if (!entries.some(function (e) { return e.isIntersecting; })) return;
      this._io.disconnect();
      this._hydrate();
    }

    /**
     * API pública: fuerza hidratación inmediata (ej. precarga del slide N+1
     * desde el script de Glide ya existente, sin tocarlo internamente).
     */
    hydrate() {
      if (this._hydrated) return;
      if (this._io) this._io.disconnect();
      this._hydrate();
    }

    _hydrate() {
      if (this._hydrated) return;
      this._hydrated = true;

      if (!this._template || !this._template.parentElement) return;

      var fragment = this._template.content.cloneNode(true);
      var video = fragment.querySelector('video');

      if (video) {
        video.setAttribute('preload', 'auto');
      }

      var poster = this.querySelector(':scope > .tuft-lazy-video__poster');
      if (poster) poster.remove();

      this._template.replaceWith(fragment);

      if (video) {
        video.load();
        var tryPlay = function () {
          var playPromise = video.play();
          if (playPromise && typeof playPromise.then === 'function') {
            playPromise.catch(function () { /* user no interactuó aún */ });
          }
        };
        if (video.readyState >= 2) {
          tryPlay();
        } else {
          video.addEventListener('loadeddata', tryPlay, { once: true });
        }
      }

      this.dispatchEvent(new CustomEvent('tuft-lazy-video:ready', { bubbles: true }));
    }
  }

  if (!customElements.get('tuft-lazy-video')) {
    customElements.define('tuft-lazy-video', TuftLazyVideo);
  }
})();
